import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Action, compose } from 'redux';
import { NO_REASON_MSG, SHOW_COLUMNS } from './constants';
import { PageHeader } from 'components/page-header';
import { Page } from 'components/page-container';
import { Button, ButtonType } from 'components/button/index';
import { InPageDialog } from 'damco-components';
import { addCostAndRevenue, resetAddCostAndRevenue } from 'actions/costRevenue';
import './VatPartnersEdit.css';
import { ThunkDispatch } from 'redux-thunk';
import { setVatPartnersTableRows, setVatPartnersTableRow } from 'actions/vat-partner-edit';
import API from 'constants/api';
import { callApiV2Put } from 'utilities/apiCaller';
import { getVatPartnerData } from 'views/job-costing/utils/CostAndRevenueHelpers';
import { generateVatPartnersUpdateErrors } from 'utilities/util';

interface IMapDispatchToProps {
	setVatPartnersTableRows: (data: any) => void;
	setVatPartnersTableRow: (data: any) => void;
	addCostAndRevenue: (data: any, targetType: string) => Promise<void>;
	resetCostRevenue: () => void;
}

const VatPartnersEdit = (props: any) => {
	const { vatPartnersOriginalData, vatPartnersEditTable, vatPartners, reasonCodes, optionsArray } = props;
	const [showDialog, setShowDialog] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [vatPartnerOptions, setVatPartnersOptions] = useState<any[]>([]);
	const [tableColumns, setTableColumns] = useState<any[]>([]);
	const [tableRows, setTableRows] = useState<any[]>([]);
	const [isValueChanged, setIsValueChanged] = useState<boolean[]>([]);

	const getColumns = (columns: any) => {
		return columns
			.filter((column: any) => SHOW_COLUMNS.includes(column.accessor))
			.map((column: any) => ({
				accessor: column.accessor,
				fieldHeader: column.fieldHeader,
			}));
	};

	const getRows = (data: any) => {
		return data.map((row: any) => ({
			...row,
		}));
	};

	const updateOptions = (originalData: any, vatPartnersOptions: any) => {
		const updatedVatPartners = [...vatPartnersOptions];
		originalData.forEach((id: string) => {
			if (!updatedVatPartners.some((partner: string) => partner.startsWith(id))) {
				updatedVatPartners.push(`${id} - ${id}`);
			}
		});
		return updatedVatPartners;
	};

	useEffect(() => {
		if (vatPartnersEditTable?.columns) {
			setTableColumns(getColumns(vatPartnersEditTable.columns));
		}
		if (vatPartnersEditTable?.data) {
			setTableRows(getRows(vatPartnersEditTable.data));
		}
	}, [vatPartnersEditTable]);

	useEffect(() => {
		setVatPartnersOptions(updateOptions(vatPartnersOriginalData, vatPartners));
	}, [vatPartnersOriginalData, vatPartners]);

	const closeDialog = () => {
		setShowDialog(false);
		setErrorMessage('');
	};

	const getVatPartnerDropdownValue = (id: string) => {
		const vatPartner = id && vatPartnerOptions.find((partner: any) => partner.startsWith(id));
		return vatPartner || '';
	};

	const handleChange = (index: number, accessor: string, value: string) => {
		const updatedRows = [...tableRows];
		updatedRows[index][accessor] = value;
		setTableRows(updatedRows);
		if (accessor === 'vatPartner') {
			vatPartnersEditTable.data[index][accessor] = value ? value.split(' - ')[0] : '';
			const originalVatPartnerValue = vatPartnersOriginalData[index];
			const updatedIsValueChanged = [...isValueChanged];
			updatedIsValueChanged[index] = value.split(' - ')[0] !== originalVatPartnerValue;
			setIsValueChanged(updatedIsValueChanged);
		}
		if(accessor === 'vatPartnerReasonCodeNo') {
			vatPartnersEditTable.data[index][accessor] = value ? getVatPartnerEditReasonCode(value) : '';
			const selectDropdowns = document.querySelectorAll('select.vat-partner-reason-code');
			setShowDialog(false);
			setErrorMessage('');
			selectDropdowns.forEach((select: any) => {
				if (select.value) {
					select.classList.remove('select-error');
				}
			});
		}
	};

	const renderCell = (row: any, accessor: string, index: number) => {
		if (accessor === 'vatPartner') {
			return (
				<select
					value={getVatPartnerDropdownValue(row.vatPartner)}
					onChange={(e) => handleChange(index, accessor, e.target.value)}
				>
					<option value="" />
					{vatPartnerOptions.map((vatPartner: any) => (
						<option key={vatPartner} value={vatPartner}>
							{vatPartner}
						</option>
					))}
				</select>
			);
		} else if (accessor === 'vatPartnerReasonCodeNo') {
			if (isValueChanged[index]) {
				return (
					<select
						className="vat-partner-reason-code"
						value={row.vatPartnerReasonCodeNo}
						onChange={(e) => handleChange(index, accessor, e.target.value)}
					>
						<option value="" />
						{optionsArray.map((reason: any) => (
							<option key={reason} value={reason}>
								{reason}
							</option>
						))}
					</select>
				);
			} else {
				return <span>&nbsp;</span>;
			}
		} else {
			return row[accessor];
		}
	};

	const getVatPartnerEditReasonCode = (description: string) => {
		const result = reasonCodes.data.find((item: any) => item.reasonDescription === description);
		return result ? result.reasonCodeNumber : null;
	};

	const saveEditedData = async (rowData: any) => {
		const selectDropdowns = document.querySelectorAll('select.vat-partner-reason-code');
		selectDropdowns.forEach((select: any) => {
			if (!select.value) {
				select.classList.add('select-error');
			}
		});
		const isAnySelectEmpty = Array.from(selectDropdowns).some((select: any) => !select.value);
		if (isAnySelectEmpty) {
			setShowDialog(isAnySelectEmpty);
			setErrorMessage(NO_REASON_MSG);
		}else{
			setShowDialog(false);
			setErrorMessage('');			
			rowData.forEach((row: any) => {
				row.updatedBy = props.nickname.employeeId;
			});
			const targetType = vatPartnersEditTable.category === 'ESSO' ? 'origin' : 'destination';
			const vatParntersUpdateData = getVatPartnerData(rowData, targetType);
			if(vatParntersUpdateData && vatParntersUpdateData.vatPartners.length > 0){
				const apiUrl = targetType === 'origin' ? API.VatPartners.updateVatPartnersEsso : API.VatPartners.updateVatPartnersIsso;
				try {		
					const vatUpdateResult = await callApiV2Put(apiUrl, vatParntersUpdateData, {});
					if(vatUpdateResult.data.length > 0){
						setShowDialog(true);
						setErrorMessage('Either the Rate Detail ID does not exist, or the Charge Item Type must be Revenue.');
					}else{
						props.history.goBack();
					}
				} catch (e) {
					const errors = generateVatPartnersUpdateErrors(e);
					setShowDialog(true);
					setErrorMessage(errors[0]);
				}	
			}else{
				props.history.goBack();
			}		
		}
	};

	return (
		<>
			<PageHeader backButton={true}>Edit VAT Partners</PageHeader>
			<Page>
				<InPageDialog
					showDialog={showDialog}
					type={'error'}
					message={errorMessage}
					closeMethod={closeDialog} />
				<table className="vat-partner-table">
					<thead>
						<tr>
							{tableColumns.map((col: any) => (
								<th key={col.accessor} className={`column-${col.accessor}`}>{col.fieldHeader}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{tableRows.map((row: any, idx: any) => (
							<tr key={idx}>
								{tableColumns.map((col: any) => (
									<td key={col.accessor} className={`column-${col.accessor}`}>{renderCell(row, col.accessor, idx)}</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
				<div className="edit-vat-table-actions">
					<Button
						isDisabled={props.disableSubmit}
						buttonType={ButtonType.Blue}
						onClick={() => {
							saveEditedData(vatPartnersEditTable.data);
						}}
					>
						Save
					</Button>
					<Button
						buttonType={ButtonType.Transparent}
						onClick={props.history.goBack}
						type="reset"
					>
						Cancel
					</Button>
				</div>
			</Page>
		</>
	);
};

const mapStateToProps = (state: any) => ({
	vatPartnersEditTable: state.vatPartnerEdit.tableData,
	vatPartnersOriginalData: state.vatPartnerEdit.originalData,
	vatPartnersData: state.vatPartnerEdit.vatPartnersData,
	vatPartners: state.vatPartnerEdit.vatPartners,
	reasonCodes: state.vatPartnerEdit.vatPartnerEditReasonCodesData,
	optionsArray: state.vatPartnerEdit.vatPartnerEditReasonOptions,
	nickname: state.nickname.userNickName,
	costRevEditSuccess: state.costRevenue.costRevEditSuccess,
	isFetchDataLoading: state.costRevenue.isFetchDataLoading,
	isFetchDataError: state.costRevenue.isFetchDataError,
	pendingError: state.costRevenue.pendingError,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): IMapDispatchToProps => ({
	addCostAndRevenue: (data: any, targetType: string) => dispatch(addCostAndRevenue(data, targetType)),
	setVatPartnersTableRows: (data: any) => dispatch(setVatPartnersTableRows(data)),
	setVatPartnersTableRow: (data: any) => dispatch(setVatPartnersTableRow(data)),
	resetCostRevenue: () => dispatch(resetAddCostAndRevenue()),
});

const enhance = compose(
	withRouter,
	connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(VatPartnersEdit);